import { TLocale } from '@northladder/utilities';

import { TThemeColorScheme } from '../types';

import { FALLBACK_COLOR_SCHEME, FALLBACK_COMPANY_ID } from './defaults';

export type TCompanyId =
  | 'none'
  | 'amazon'
  | 'dynamica'
  | 'etisalat'
  | 'jumbo'
  | 'ksp'
  | 'mms'
  | 'northladder'
  | 'renewed'
  | 'spark'
  | 'samsung'
  | 'stc';

export interface IAppEnvConfig {
  companyId: TCompanyId;
  configKey: string;
  colorScheme: TThemeColorScheme;
  flagLogoUrl: string;
}

interface IAppConfig {
  companyId: TCompanyId;
  companyName: string;
  branding: {
    colorScheme: TThemeColorScheme;
    /**
     * Trying to get the favicon from remote is a bit challenging...
     * https://nextjs.org/docs/app/api-reference/file-conventions/metadata/app-icons#favicon
     */
    favicon: string /** Might not be possible */;
    flag: string;
    logo: string;
  };
}

export interface IAppConfigBasePaths {
  genericCdn: string;
  genericPrivateCdn: string;
  genericService: string;
  partnerCdn: string;
  partnerService: string;
}

export interface IAppConfigCommon {
  companyId: string;
  companyName: string;
  colorScheme: string;
  locales: TLocale[];
}

/**
 * Configuration for a single menu item visibility and enabled state.
 */
interface IMenuItemConfig {
  isEnabled: boolean;
  isVisible: boolean;
}

/**
 * Configuration for bids.
 */
export interface IAppConfigBids {
  canPlaceSingleBid: boolean;
  canViewBidsEfficiency: boolean;
  canViewBidsListOptionsMenu: boolean;
  canViewBidsStagedEfficiency: boolean;
  canViewBidsStagedReport: boolean;
  canViewHotDeals: boolean;
  canViewSimilarBids: boolean;
  canViewStagedBids: boolean;
  canViewStagedBidsOptionsMenu: boolean;
  isExportEnabled: boolean;
  isRankOneBidDisplayEnabled: boolean;
}

/**
 * Configuration for account statements.
 */
export interface IAppConfigAccountStatements {
  isExportEnabled: boolean;
}

/**
 * Configuration for deals.
 */
export interface IAppConfigDeals {
  canViewAssessmentImages: boolean;
  canViewDealsListOptionsMenu: boolean;
}

/**
 * Configuration for transactions.
 */
export interface IAppConfigTransactions {
  canViewTransactions: boolean;
}

/**
 * Configuration for grade review requests.
 */
export interface IAppConfigGradeReviewRequests {
  canViewGradeReviewRequests: boolean;
  canEditGradeReviewRequests: boolean;
  canCreateGradeReviewRequests: boolean;
}

/**
 * Configuration for notifications.
 */
export interface IAppConfigNotifications {
  canViewOwnAccountNotifications: boolean;
}

/**
 * Configuration for login and signup.
 */
export interface IAppConfigLoginAndSignup {
  isForgetAccountEnabled: boolean;
  isSignupEnabled: boolean;
  isSupportAvailable: boolean;
}

/**
 * Configuration for sidebar menu visibility and enabled state.
 */
export interface IAppConfigSidebarFeature {
  accountStatements: IMenuItemConfig;
  allBids: IMenuItemConfig;
  allDeals: IMenuItemConfig;
  allRequests: IMenuItemConfig;
  allTransactions: IMenuItemConfig;
  bidEfficiency: IMenuItemConfig;
  bidsSection: IMenuItemConfig;
  dashboardRecentActivity: IMenuItemConfig;
  dashboardSection: IMenuItemConfig;
  dashboardStatistics: IMenuItemConfig;
  dealsDelivered: IMenuItemConfig;
  dealsInTransit: IMenuItemConfig;
  dealsSection: IMenuItemConfig;
  gradeReviewHelp: IMenuItemConfig;
  gradeReviewSection: IMenuItemConfig;
  myProfile: IMenuItemConfig;
  myUploads: IMenuItemConfig;
  notifications: IMenuItemConfig;
  requestAReview: IMenuItemConfig;
  settingsSection: IMenuItemConfig;
  stagedBids: IMenuItemConfig;
  stagedBidsReport: IMenuItemConfig;
  transactionsInTransit: IMenuItemConfig;
  transactionsSection: IMenuItemConfig;
}

export interface IAppConfigProfile {
  canChangePassword: boolean;
  isPaymentEditAllowed: boolean;
  isProfileEditAllowed: boolean;
}

export interface IAppConfigBuyer {
  canSwitchColorScheme: boolean;
  isBetaTester: boolean;
  loginAndSignup: IAppConfigLoginAndSignup;
}

// TODO: Merge this with IAppConfigBuyer
export interface IAppConfigDealer {
  contactInformation: {
    contactNumber: string;
    whatsappNumber: string;
    email: string;
    address: string;
  };
  workingHours: {
    days: {
      startDay: string;
      endDay: string;
    };
    time: {
      startTime: string;
      startPeriod: 'am' | 'pm';
      endTime: string;
      endPeriod: 'am' | 'pm';
    };
  };
  termsAndConditions: {
    filePath: string;
    version: number;
  };
}

export interface IAppTenantConfig {
  tenantId: string;
  isActive: boolean;
  defaultCurrency: string;
  configuration: {
    common: {
      basePath: IAppConfigBasePaths;
      phoneNumberPrefixes: string[];
    };
    apps: {
      common: IAppConfigCommon;
      buyer: IAppConfigBuyer;
      dealer: IAppConfigDealer;
    };
  };
}

interface IRequestRevalidationOptions extends RequestInit {
  next?: { revalidate?: number };
}

/**
 * Fetch the application configuration from the CDN.
 */
async function fetchTenantAppConfigFromCDN(
  configKey: string
): Promise<IAppConfig | null> {
  const endpoint = `${process.env.NEXT_PUBLIC_TENANT_CONFIG_BASE_URL}/${configKey}.json`;

  const response = await fetch(endpoint, {
    next: { revalidate: 60 },
  } as IRequestRevalidationOptions);

  if (!response.ok) {
    // eslint-disable-next-line no-console
    console.error(
      '⚠️:Error:fetchTenantAppConfigFromCDN: Failed to fetch app config',
      {
        endpoint,
        status: response.status,
        statusText: response.statusText,
        url: response.url,
      }
    );

    throw new Error('Fetch fetchTenantAppConfigFromCDN Error');
  }

  // TODO: Return directly after debugging this.
  const tenantData = (await response.json()) as IAppConfig;

  return tenantData;
}

/**
 * This fetches the company configuration from the environment variable and
 * loads it on the initialization of the app on the server-side.
 *
 * This is used to set the color scheme of the app, which is used to determine
 * the theme for the app and the theme for the UI components.
 *
 * - NOTE: This will later be fetched from a remote API configuration service.
 */
export const getRemoteAppConfig = async (): Promise<IAppEnvConfig | null> => {
  try {
    /**
     * The configuration string is fetched from the environment variable
     * `NEXT_PUBLIC_COMPANY_CONFIG`. This is expected to be a JSON string
     * containing the company configuration, but can later be extracted out and
     * generated from a URL route param (Worst case scenario).
     */
    const configStr = process.env.NEXT_PUBLIC_COMPANY_CONFIG;

    if (!configStr) {
      // TODO: Integrate a better logging library. This is a phase 1 hack for now.
      // eslint-disable-next-line no-console
      console.error('⚠️::NEXT_PUBLIC_COMPANY_CONFIG env key is not set.');

      return null;
    }

    const config = JSON.parse(configStr) as IAppEnvConfig;
    const companyId = config.companyId || FALLBACK_COMPANY_ID;

    // TODO: Or get this from url param
    // TODO: Since this is server site, cannot get it outside page.tsx
    // TODO: Find a workaround this, of feed this as initial data in the tenant-query
    const configKey = config.configKey || '';
    const colorScheme = config.colorScheme || FALLBACK_COLOR_SCHEME;
    const flagLogoUrl = config.flagLogoUrl || '';

    const remoteConfig = await fetchTenantAppConfigFromCDN(configKey);

    return {
      colorScheme: remoteConfig?.branding?.colorScheme || colorScheme,
      configKey,
      companyId: remoteConfig?.companyId || companyId,
      flagLogoUrl,

      // remoteConfig,
    };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(
      '⚠️::NEXT_PUBLIC_COMPANY_CONFIG key is invalid. You likely have not set it',
      error
    );

    return null;
  }
};
